import { useState, useEffect, useRef, useCallback } from 'react';
import { GxFeedItem, getFeedUpdates } from '../api/guestExperience';

const BASE_INTERVAL_MS = 5000;
const MAX_INTERVAL_MS = 60000;

interface UseGxPollingResult {
  lastPollTime: Date | null;
  isPolling: boolean;
  error: Error | null;
}

export function useGxPolling(
  eventId: string,
  enabled: boolean,
  onNewItems: (items: GxFeedItem[]) => void,
): UseGxPollingResult {
  const [lastPollTime, setLastPollTime] = useState<Date | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const timeoutRef = useRef<number | null>(null);
  const currentIntervalMs = useRef(BASE_INTERVAL_MS);
  const lastPollTimeRef = useRef<string | null>(null);
  const onNewItemsRef = useRef(onNewItems);
  const enabledRef = useRef(enabled);

  // Keep refs up to date without triggering re-renders
  useEffect(() => {
    onNewItemsRef.current = onNewItems;
  }, [onNewItems]);

  useEffect(() => {
    enabledRef.current = enabled;
  }, [enabled]);

  const scheduleNext = useCallback(() => {
    if (!enabledRef.current) return;
    timeoutRef.current = window.setTimeout(() => {
      poll();
    }, currentIntervalMs.current);
  }, []);

  const poll = useCallback(async () => {
    if (!eventId || !lastPollTimeRef.current || !enabledRef.current) return;
    setIsPolling(true);
    try {
      const newItems = await getFeedUpdates(eventId, lastPollTimeRef.current);
      const now = new Date();
      lastPollTimeRef.current = now.toISOString();
      setLastPollTime(now);
      setError(null);
      // Reset to base interval on success
      currentIntervalMs.current = BASE_INTERVAL_MS;

      if (newItems.length > 0) {
        onNewItemsRef.current(newItems);
      }
    } catch (err) {
      let errorMessage = 'Connection error';
      if (err instanceof Error) {
        errorMessage = err.message;
      } else if (err && typeof err === 'object' && 'message' in err) {
        errorMessage = String((err as { message: unknown }).message);
      }
      const pollError = new Error(errorMessage);
      setError(pollError);
      // Exponential backoff on error: 5s → 10s → 20s → 40s → 60s (max)
      currentIntervalMs.current = Math.min(
        currentIntervalMs.current * 2,
        MAX_INTERVAL_MS,
      );
    } finally {
      setIsPolling(false);
      scheduleNext();
    }
  }, [eventId, scheduleNext]);

  useEffect(() => {
    if (!enabled) {
      if (timeoutRef.current !== null) {
        window.clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      return;
    }

    // Initialize lastPollTime on enable
    const now = new Date();
    lastPollTimeRef.current = now.toISOString();
    setLastPollTime(now);
    currentIntervalMs.current = BASE_INTERVAL_MS;

    // Schedule first poll
    timeoutRef.current = window.setTimeout(() => {
      poll();
    }, currentIntervalMs.current);

    return () => {
      if (timeoutRef.current !== null) {
        window.clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [enabled, poll]);

  return { lastPollTime, isPolling, error };
}
